import React, { useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import {
  API_EVALUATION_REQUESTS_QUERY,
  API_EVALUATION_REQUEST_QUERY,
  EXPORT_API_EVALUATIONS_CSV_QUERY,
  APIEvaluationRequestItem,
  APIEvaluationRequestDetail,
} from '../graphql/settings';
import Grid from '@mui/material/Grid';

const TIME_RANGE_OPTIONS = [
  { value: '30_min', label: 'Last 30 Minutes' },
  { value: '1_hour', label: 'Last Hour' },
  { value: '24_hours', label: 'Last 24 Hours' },
  { value: '1_week', label: 'Last Week' },
  { value: '30_days', label: 'Last 30 Days' },
  { value: 'all_time', label: 'All Time' },
];

const ApiResults: React.FC = () => {
  const [timeRange, setTimeRange] = useState('30_days');
  const [evaluationKeyFilter, setEvaluationKeyFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [limit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const { data, loading, error } = useQuery(API_EVALUATION_REQUESTS_QUERY, {
    variables: {
      limit,
      offset,
      timeRange,
      evaluationKey: evaluationKeyFilter || undefined,
    },
    pollInterval: 30000,
    notifyOnNetworkStatusChange: false, // Avoid re-renders during refetch that disrupt typing
  });

  const { data: detailData, loading: detailLoading } = useQuery(API_EVALUATION_REQUEST_QUERY, {
    variables: { id: selectedId },
    skip: !selectedId,
  });

  const [fetchCsv] = useLazyQuery(EXPORT_API_EVALUATIONS_CSV_QUERY, { fetchPolicy: 'network-only' });

  const requests: APIEvaluationRequestItem[] = data?.apiEvaluationRequests ?? [];
  const total = data?.totalApiEvaluationRequests ?? 0;
  const detail = detailData?.apiEvaluationRequest as APIEvaluationRequestDetail | undefined;

  const evaluationKeys = Array.from(
    new Set(requests.map((r) => r.evaluationKey).filter(Boolean))
  ).sort();

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const { data: csvData } = await fetchCsv({
        variables: { timeRange, evaluationKey: evaluationKeyFilter || undefined },
      });
      if (csvData?.exportApiEvaluationsCsv) {
        const blob = new Blob([csvData.exportApiEvaluationsCsv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `api-evaluations-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          API Evaluations
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Time range</InputLabel>
            <Select
              value={timeRange}
              label="Time range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              {TIME_RANGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Evaluation key</InputLabel>
            <Select
              value={evaluationKeyFilter}
              label="Evaluation key"
              onChange={(e) => setEvaluationKeyFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {evaluationKeys.map((k) => (
                <MenuItem key={k} value={k}>{k}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Export to CSV">
            <span>
              <Button
                data-ga-label="ApiResults Export CSV"
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleExportCsv}
                disabled={exporting || requests.length === 0}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Results from evaluation API submissions (request/response payloads). Same analysis as sequence runs.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Evaluation key</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Safe</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Harm score</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Compliance</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>User Perception</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Custom tests</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedId(r.id)}
                    >
                      <TableCell>{r.evaluationKey}</TableCell>
                      <TableCell>{r.evaluationName || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size="small"
                          color={r.status === 'completed' ? 'success' : r.status === 'failed' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                      <TableCell>
                        {r.status === 'completed' ? (r.isSafe ? '✅' : '❌') : '—'}
                      </TableCell>
                      <TableCell>
                        {r.status === 'completed' && r.overallHarmScore != null ? `${Math.round(r.overallHarmScore)}%` : '—'}
                      </TableCell>
                      <TableCell>
                        {r.status === 'completed' ? (r.graniteRiskDetected ? '❌' : '✅') : '—'}
                      </TableCell>
                      <TableCell>
                        {r.status === 'completed' ? (r.llmSemanticAllPassed == null ? '—' : r.llmSemanticAllPassed ? '✅' : '❌') : '—'}
                      </TableCell>
                      <TableCell>
                        {r.status === 'completed' && r.customTestsPerformed
                          ? (r.customTestsAllPassed == null ? '—' : r.customTestsAllPassed ? '✅' : '❌')
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button data-ga-label="ApiResults View" size="small" onClick={(e) => { e.stopPropagation(); setSelectedId(r.id); }}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {requests.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No API evaluation results in this period. Submit requests via the evaluation API to see results here.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {total > requests.length && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Showing {requests.length} of {total} results.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selectedId} onClose={() => setSelectedId(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          API Evaluation — {detail?.evaluationKey ?? selectedId}
        </DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Box sx={{ mb: 2 }}>
                <Chip label={detail.status} size="small" color={detail.status === 'completed' ? 'success' : 'default'} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary" component="span">
                  Created: {new Date(detail.createdAt).toLocaleString()}
                  {detail.completedAt && ` · Completed: ${new Date(detail.completedAt).toLocaleString()}`}
                </Typography>
              </Box>
              {detail.errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>{detail.errorMessage}</Alert>
              )}
              {detail.status === 'completed' && (
                <>
                  {/* At-a-glance summary */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
                    <Chip
                      size="small"
                      label={detail.isSafe ? 'Safe' : 'Unsafe'}
                      color={detail.isSafe ? 'success' : 'error'}
                      variant="outlined"
                    />
                    {detail.overallHarmScore != null && (
                      <Chip size="small" label={`Harm ${Math.round(detail.overallHarmScore)}%`} variant="outlined" />
                    )}
                    <Chip
                      size="small"
                      label={detail.graniteRiskDetected ? 'Compliance risk' : 'Compliance OK'}
                      color={detail.graniteRiskDetected ? 'error' : 'success'}
                      variant="outlined"
                    />
                    {detail.llmSemanticAllPassed != null && (
                      <Chip
                        size="small"
                        label={detail.llmSemanticAllPassed ? 'Semantic OK' : 'Semantic issues'}
                        color={detail.llmSemanticAllPassed ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                    {detail.customTestsPerformed && detail.customTestsAllPassed != null && (
                      <Chip
                        size="small"
                        label={detail.customTestsAllPassed ? 'Custom tests OK' : 'Custom tests failed'}
                        color={detail.customTestsAllPassed ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* AI Risk Analysis */}
                  {detail.harmAnalysisPerformed && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
                        AI Risk Analysis
                      </Typography>
                      <Paper sx={{ p: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'error.light', borderRadius: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {detail.isSafe
                            ? 'All 13 harm categories passed'
                            : `${detail.harmCategoryFailures} of 13 categories failed, ${detail.harmCategoryPasses} passed`}
                          {detail.overallHarmScore != null && ` · Score: ${Math.round(detail.overallHarmScore)}%`}
                        </Typography>
                        {detail.failedCategoryNames?.length > 0 && (
                          <Box sx={{ mt: 1, mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main', display: 'block', mb: 0.5 }}>
                              Failed: {detail.failedCategoryNames.join(', ')}
                            </Typography>
                          </Box>
                        )}
                        {detail.passedCategoryNames?.length > 0 && (
                          <Box sx={{ mt: 0.5, mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main', display: 'block', mb: 0.5 }}>
                              Passed: {detail.passedCategoryNames.join(', ')}
                            </Typography>
                          </Box>
                        )}
                        {detail.ignoredCategoryNames?.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Ignored: {detail.ignoredCategoryNames.join(', ')}
                          </Typography>
                        )}
                        {detail.harmAnalysisDetails && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                            {detail.harmAnalysisDetails}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {/* Standards & Regulatory Compliance */}
                  {detail.graniteAnalysisPerformed && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
                        Standards & Regulatory Compliance
                      </Typography>
                      <Paper sx={{ p: 1.5, bgcolor: 'background.paper', border: '1px solid #1976d2', borderRadius: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {!detail.graniteRiskDetected
                            ? `All ${detail.graniteCategoriesChecked} risk categories passed`
                            : `${detail.graniteCategoriesFailed} of ${detail.graniteCategoriesChecked} categories flagged`}
                          {detail.graniteOverallScore != null && ` · Score: ${Math.round(detail.graniteOverallScore)}%`}
                        </Typography>
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          {[
                            { key: 'graniteHarm', label: 'Harmful Content', value: detail.graniteHarm },
                            { key: 'graniteSocialBias', label: 'Social Bias', value: detail.graniteSocialBias },
                            { key: 'graniteJailbreak', label: 'Jailbreak', value: detail.graniteJailbreak },
                            { key: 'graniteViolence', label: 'Violence', value: detail.graniteViolence },
                            { key: 'graniteProfanity', label: 'Profanity', value: detail.graniteProfanity },
                            { key: 'graniteSexualContent', label: 'Sexual Content', value: detail.graniteSexualContent },
                            { key: 'graniteUnethicalBehavior', label: 'Unethical Behavior', value: detail.graniteUnethicalBehavior },
                          ].map(({ key, label, value }) => (
                            <Grid item xs={12} sm={6} key={key}>
                              <Typography variant="caption" sx={{ color: value === true ? 'error.main' : value === false ? 'success.main' : 'text.secondary' }}>
                                {value === true ? '❌' : value === false ? '✅' : '—'} {label}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Box>
                  )}

                  {/* User Perception & Experience (LLM Semantic) */}
                  {(detail.llmSemanticAnalysisPerformed || detail.embeddingAnalysisPerformed) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#9c27b0' }}>
                        User Perception & Experience
                      </Typography>
                      <Paper sx={{ p: 1.5, bgcolor: 'background.paper', border: '1px solid #9c27b0', borderRadius: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          {detail.llmSemanticAllPassed !== false ? 'All semantic checks passed' : 'One or more semantic issues detected'}
                        </Typography>
                        <Grid container spacing={1.5}>
                          {detail.llmSemanticAnalysisPerformed && (
                            <>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: detail.llmGroundednessPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)', border: `1px solid ${detail.llmGroundednessPassed !== false ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}` }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {detail.llmGroundednessPassed !== false ? '✅' : '❌'} Grounded
                                  </Typography>
                                  {detail.llmGroundednessPassed === false && detail.llmGroundednessExplanation && (
                                    <Typography variant="caption" display="block" sx={{ color: 'error.dark', fontStyle: 'italic', mt: 0.5 }}>
                                      {detail.llmGroundednessExplanation}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: detail.llmHallucinationPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)', border: `1px solid ${detail.llmHallucinationPassed !== false ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}` }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {detail.llmHallucinationPassed !== false ? '✅' : '❌'} No Hallucination
                                  </Typography>
                                  {detail.llmHallucinationPassed === false && detail.llmHallucinationExplanation && (
                                    <Typography variant="caption" display="block" sx={{ color: 'error.dark', fontStyle: 'italic', mt: 0.5 }}>
                                      {detail.llmHallucinationExplanation}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: detail.llmTonePassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)', border: `1px solid ${detail.llmTonePassed !== false ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}` }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {detail.llmTonePassed !== false ? '✅' : '❌'} Appropriate Tone
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1, bgcolor: detail.llmSemanticAllPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)', border: `1px solid ${detail.llmSemanticAllPassed !== false ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}` }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {detail.llmSemanticAllPassed !== false ? '✅' : '❌'} Unbiased
                                  </Typography>
                                  {detail.llmBiasScore != null && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Score: {Math.round(detail.llmBiasScore)}%
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>
                    </Box>
                  )}

                  {detail.customTestsPerformed && Array.isArray(detail.customTestsResults) && detail.customTestsResults.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#00695c' }}>
                        Custom assertions
                      </Typography>
                      <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {detail.customTestsAllPassed
                            ? 'All configured checks passed.'
                            : 'One or more custom checks failed.'}
                        </Typography>
                        {(detail.customTestsResults || []).map((row, idx) => (
                          <Box
                            key={`${row.id || idx}-${row.type || ''}`}
                            sx={{
                              py: 0.75,
                              borderTop: idx === 0 ? 'none' : '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                              {row.passed ? '✅' : '❌'} {row.type || 'test'}
                              {row.id ? ` · ${row.id}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {row.message}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}
                </>
              )}
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Input (request JSON)</AccordionSummary>
                <AccordionDetails>
                  <Box component="pre" sx={{ overflow: 'auto', maxHeight: 300, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.8rem' }}>
                    {JSON.stringify(detail.inputData, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Output (response JSON)</AccordionSummary>
                <AccordionDetails>
                  <Box component="pre" sx={{ overflow: 'auto', maxHeight: 300, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.8rem' }}>
                    {JSON.stringify(detail.outputData, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}
          {selectedId && (detailLoading || (!detail && !error)) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-ga-label="ApiResults Close" onClick={() => setSelectedId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiResults;
